import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { useFlashMessage } from '../context/FlashMessageContext';
import {
  LogOut,
  Share2,
  KeyRound,
  Home,
  Shield,
  Fingerprint,
  Building2,
  Copy,
  Check,
} from 'lucide-react';

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {'dashboard'|'share'|'credentials'|'trusted-devices'|'profile-update'|'tenant-configurations'} props.activeNav
 */
export default function DashboardLayout({ children, activeNav }) {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { showSuccess } = useFlashMessage();
  const [copiedUserId, setCopiedUserId] = useState(false);

  const navClass = (key) => (activeNav === key ? 'active' : '');

  const handleCopyUserId = async () => {
    if (!user?.userId) return;
    try {
      await navigator.clipboard.writeText(user.userId);
      setCopiedUserId(true);
      showSuccess('User ID copied to clipboard!');
      setTimeout(() => setCopiedUserId(false), 2000);
    } catch {
      showSuccess(`User ID: ${user.userId}`);
    }
  };

  return (
    <div className="dashboard-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="avatar">{user?.fullName?.charAt(0) || '?'}</div>
          <h2>{user?.fullName || 'User'}</h2>
          <p>{user?.email || ''}</p>
          {user?.userId && (
            <div className="user-id-container">
              <p className="user-id">ID: {user.userId}</p>
              <button
                type="button"
                className="copy-user-id-btn"
                onClick={handleCopyUserId}
                title="Copy User ID to clipboard"
                aria-label="Copy User ID to clipboard"
              >
                {copiedUserId ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          )}
        </div>
        <nav className="sidebar-nav">
          <ul>
            <li>
              <a href="#dashboard" className={navClass('dashboard')} onClick={() => navigate('/dashboard')}>
                <Home size={20} /> Dashboard
              </a>
            </li>
            <li>
              <a href="#share" className={navClass('share')} onClick={() => navigate('/dashboard/share')}>
                <Share2 size={20} /> Shared Credentials
              </a>
            </li>
            {/* Hidden from sidebar — route /dashboard/credentials still works if opened directly.
            <li>
              <a
                href="#credentials"
                className={navClass('credentials')}
                onClick={() => navigate('/dashboard/credentials')}
              >
                <KeyRound size={20} /> My Credentials
              </a>
            </li>
            */}
            <li>
              <a
                href="#trusted-devices"
                className={navClass('trusted-devices')}
                onClick={() => navigate('/dashboard/trusted-devices')}
              >
                <Shield size={20} /> Trusted Devices
              </a>
            </li>
            <li>
              <a
                href="#profile-update"
                className={navClass('profile-update')}
                onClick={() => navigate('/dashboard/profile-update')}
              >
                <Fingerprint size={20} /> Profile Update
              </a>
            </li>
            <li>
              <a
                href="#tenant-configurations"
                className={navClass('tenant-configurations')}
                onClick={() => navigate('/dashboard/tenant-configurations')}
              >
                <Building2 size={20} /> Tenants / configurations
              </a>
            </li>
          </ul>
        </nav>
        <div style={{ marginTop: 'auto' }}>
          <button type="button" onClick={logout} className="sidebar-nav-item">
            <LogOut size={20} /> Logout
          </button>
        </div>
      </aside>
      <main className="main-content">{children}</main>
    </div>
  );
}
